import { RiAlertFill, RiInformationLine } from '@remixicon/react';
import { PropsWithChildren, ReactNode } from 'react';
import { AgSwitch, AgText } from 'src/components/ui';
import { AgRadio, AgRadioGroup } from 'src/components/ui/AgRadio';
import { useQuickbooksPreferences, useUpdateQuickbooksPreferences } from './quickbooks.api';

type ReferenceFormat = 'PRIVATE_NOTE' | 'DOC_NUMBER';

export default function QBOPreferencesTab() {
  const { data, loading } = useQuickbooksPreferences();
  const [updatePreferences] = useUpdateQuickbooksPreferences();

  const referenceFormat: ReferenceFormat = data?.quickbooksPreferences?.referenceFormat ?? 'PRIVATE_NOTE';
  const syncDraftBills: boolean = data?.quickbooksPreferences?.syncDraftBills ?? false;

  const handleReferenceFormatChange = (value: string) => {
    updatePreferences({ variables: { input: { referenceFormat: value } } });
  };

  const handleSyncDraftBillsChange = (value: boolean) => {
    updatePreferences({ variables: { input: { syncDraftBills: value } } });
  };

  if (loading) return null;

  return (
    <div className="card p-6 flex flex-col gap-6">
      <PreferencesSection
        label="Reference Number Format"
        subtitle={'Choose where your Agriful Reference # appears in QuickBooks when a document is synced.'}
        leftChildren={<ReferencePreviewCard format={referenceFormat} />}
      >
        <AgRadioGroup value={referenceFormat} onChange={handleReferenceFormatChange}>
          <ReferenceOptionCard
            value="PRIVATE_NOTE"
            title="Private Note"
            recommended
            description="Your Agriful reference is stored in the QuickBooks memo field. QuickBooks keeps its own sequential number (e.g. 1047)."
            selected={referenceFormat === 'PRIVATE_NOTE'}
          />
          <ReferenceOptionCard
            value="DOC_NUMBER"
            title="Document Number"
            description="Your Agriful reference (e.g. INV-1234) becomes the primary invoice number in QuickBooks. QBO's auto-sequential numbering is disabled."
            warning="QuickBooks auto-numbering will be turned off"
            selected={referenceFormat === 'DOC_NUMBER'}
          />
        </AgRadioGroup>
      </PreferencesSection>

      <div className="h-px bg-neutral-5" />

      <PreferencesSection label="Sync settings" subtitle="Configure sync specifics to your preference">
        <div className="flex flex-col gap-1">
          <AgSwitch
            size="md"
            label="Sync draft bills to QuickBooks"
            isSelected={syncDraftBills}
            onChange={handleSyncDraftBillsChange}
          />
          <AgText size="14" color="secondary" className="pl-48">
            When enabled, bills are sent to QuickBooks as soon as they&apos;re created, before they are marked as
            &ldquo;Open&rdquo;.
          </AgText>
        </div>
      </PreferencesSection>
    </div>
  );
}

type PreferencesSectionProps = PropsWithChildren<{ label: string; subtitle?: ReactNode; leftChildren?: ReactNode }>;

function PreferencesSection({ label, subtitle, leftChildren, children }: PreferencesSectionProps) {
  return (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="flex flex-col gap-2 md:w-2/5 shrink-0">
        <AgText size="14" weight="semibold">
          {label}
        </AgText>
        {subtitle && (
          <AgText size="14" color="secondary">
            {subtitle}
          </AgText>
        )}
        {leftChildren && <div className="mt-2">{leftChildren}</div>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function ReferencePreviewCard({ format }: { format: ReferenceFormat }) {
  const invoiceNumber = format === 'PRIVATE_NOTE' ? '1047' : 'INV-1234';
  const memo = format === 'PRIVATE_NOTE' ? 'Agriful Invoice #: INV-1234' : '—';

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Invoice #', value: invoiceNumber },
    { label: 'Invoice Date', value: '04/01/2026' },
    { label: 'Due Date', value: '05/01/2026' },
    { label: 'Memo', value: memo },
    { label: 'Total', value: '$2,450.00' },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md border border-neutral-5 overflow-hidden bg-white">
        <div className="px-3 py-2 border-b border-neutral-5 bg-brand-alpha-3">
          <AgText size="12" className="text-brand-8">
            Preview how your reference will appear in QuickBooks
          </AgText>
        </div>
        <div className="flex flex-col">
          {rows.map((row, i) => (
            <div
              key={row.label}
              className={`flex items-center justify-between px-3 py-2 ${
                i < rows.length - 1 ? 'border-b border-neutral-5' : ''
              }`}
            >
              <AgText size="13" color="secondary">
                {row.label}
              </AgText>
              <AgText size="13" weight="medium">
                {row.value}
              </AgText>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <RiInformationLine size={14} className="text-neutral-alpha-9 shrink-0" />
        <AgText size="12" color="secondary">
          Preview updates as you select options on the right
        </AgText>
      </div>
    </div>
  );
}

type ReferenceOptionCardProps = {
  value: ReferenceFormat;
  title: string;
  description: string;
  recommended?: boolean;
  warning?: string;
  selected: boolean;
};

function ReferenceOptionCard({ value, title, description, recommended, warning, selected }: ReferenceOptionCardProps) {
  return (
    <AgRadio
      value={value}
      className={`rounded-md border p-4 transition-colors items-start gap-3 w-full ${
        selected ? 'border-brand-6' : 'border-neutral-5'
      }`}
      label={
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="flex items-center gap-2">
            <AgText size="14" weight="semibold">
              {title}
            </AgText>
            {recommended && <span className="badge badge-green">Recommended</span>}
          </div>
          <AgText size="14" color="secondary">
            {description}
          </AgText>
          {warning && (
            <div className="flex items-center gap-1.5 mt-1">
              <RiAlertFill size={16} className="text-orange-icon shrink-0" />
              <AgText size="13" weight="medium" className="text-orange-text">
                {warning}
              </AgText>
            </div>
          )}
        </div>
      }
    />
  );
}
